import {
  buyPrice,
  findFreePlacement,
  getItemDef,
  getQuest,
  MAX_TIER,
  offerFor,
  offerLock,
  repToReachTier,
  TIER_NAMES,
  traderRep,
  traderTier,
  type GridState,
  type LoyaltyState,
  type OfferStanding,
  type QuestState,
  type TraderDef,
} from "@rietz/shared";

/**
 * The one question the shelf keeps asking: can this be bought right now, and
 * if not, in one phrase, why. Pure, because FOUR places must agree — the tile,
 * the Buy button, the right-click entry and the drop answer over the stash.
 * The order is the order of the walls, hardest first: the three shelf gates
 * (money is a raid away and room a drag away, but a gated line cannot be
 * bought at any price today), then money, then room. Within the gates
 * `offerLock` fixes level → tier → quest, the same order the server refuses in.
 */
export type OfferProblem =
  | `Level ${number} required`
  | `Loyalty tier ${number} required`
  | `Requires quest "${string}"`
  | "Not enough money"
  | "No room in stash";

/**
 * `null` when `trader` would hand `defId` over for `euros` right now. Room is
 * asked of the stash AS IT IS with the server's own first-fit walk
 * (`findFreePlacement`); a stash that has not arrived, or an unknown def, has
 * no room. `standing` is assembled by `traderStanding` from the same profile
 * fields the server uses. An offer the trader does not stock falls through to
 * the room check.
 */
export function offerProblem(
  trader: TraderDef,
  defId: string,
  euros: number,
  stash: GridState | null,
  standing: OfferStanding,
): OfferProblem | null {
  const offer = offerFor(trader, defId);
  if (offer !== null) {
    switch (offerLock(offer, standing)) {
      case "level":
        return `Level ${offer.minLevel ?? 1} required`;
      case "tier":
        return `Loyalty tier ${offer.minTier ?? 1} required`;
      case "quest": {
        // The quest's NAME, never its id; the id is the fallback for lost
        // content.
        const questId = offer.afterQuest ?? "";
        return `Requires quest "${getQuest(questId)?.name ?? questId}"`;
      }
      default:
        break;
    }
  }
  if (euros < buyPrice(trader, defId)) return "Not enough money";
  const def = getItemDef(defId);
  if (def === null || stash === null) return "No room in stash";
  return findFreePlacement(stash, def) === null ? "No room in stash" : null;
}

/**
 * The level this shelf line opens at, or `null` when open to everybody — the
 * tile draws a "Lvl 12" tag from it, and "no tag" differs from "1". A fact
 * about the OFFER, so the tag stays after the gate opens.
 */
export function offerMinLevel(trader: TraderDef, defId: string): number | null {
  const min = offerFor(trader, defId)?.minLevel;
  return min === undefined || min <= 1 ? null : min;
}

/**
 * One corner tag on a shelf tile: what the gate says, whether it is shut for
 * this player, and for the quest the longer sentence a hover gives.
 */
export interface OfferGateTag {
  text: string;
  shut: boolean;
  title?: string;
}

/**
 * Every gate a shelf line carries, in `offerLock` order. ONE TAG PER GATE — a
 * refusal is one sentence, but a shop window should say all three. The tags
 * stay after they open; `shut` is asked GATE BY GATE, so an unmet level with a
 * done quest draws one padlock, not three.
 */
export function offerGateTags(
  trader: TraderDef,
  defId: string,
  standing: OfferStanding,
): OfferGateTag[] {
  const offer = offerFor(trader, defId);
  if (offer === null) return [];
  const tags: OfferGateTag[] = [];

  const minLevel = offer.minLevel;
  if (minLevel !== undefined && minLevel > 1) {
    tags.push({ text: `Lvl ${minLevel}`, shut: standing.level < minLevel });
  }

  const minTier = offer.minTier;
  if (minTier !== undefined && minTier > 1) {
    // The NUMBER here and the NAME in the header: a nine-pixel corner has room
    // for "Tier 3", not "Regular".
    tags.push({ text: `Tier ${minTier}`, shut: standing.tier < minTier });
  }

  const questId = offer.afterQuest;
  if (questId !== undefined && questId.length > 0) {
    tags.push({
      text: "Quest",
      shut: !standing.isQuestDone(questId),
      title: getQuest(questId)?.name ?? questId,
    });
  }

  return tags;
}

/**
 * The header block over one trader's shelf: where you stand with this man, and
 * how far the next rung is. All derived.
 */
export interface LoyaltyView {
  tier: number;
  /** `TIER_NAMES[tier - 1]` — "Stranger" through "Trusted". */
  name: string;
  rep: number;
  /** The rung above, or `null` at the top of the ladder. */
  nextTier: number | null;
  /** Reputation still to earn for `nextTier`, or `null` at the top. */
  repToNext: number | null;
  /**
   * How far along this rung, 0..1. Exactly 1 at the top — an empty bar under
   * "Trusted" would read as a standing just lost.
   */
  fraction: number;
}

/**
 * The trader header's numbers, worked out away from the JSX so the bar and the
 * caption answer the SAME question. The fraction is measured FROM THIS RUNG'S
 * FLOOR, not zero — 74 of 150 for Regular is a quarter of the way through the
 * 50 → 150 rung.
 */
export function loyaltyView(
  loyalty: LoyaltyState,
  quests: readonly QuestState[],
  traderId: string,
): LoyaltyView {
  const rep = traderRep(loyalty, quests, traderId);
  const tier = traderTier(loyalty, quests, traderId);
  const name = TIER_NAMES[tier - 1] ?? TIER_NAMES[0] ?? "";

  // Tier 1 has no threshold (`repToReachTier` answers `null`), so its floor is
  // 0.
  const floor = repToReachTier(tier) ?? 0;
  const nextTier = tier < MAX_TIER ? tier + 1 : null;
  const need = nextTier === null ? null : repToReachTier(nextTier);
  if (nextTier === null || need === null || need <= floor) {
    return { tier, name, rep, nextTier: null, repToNext: null, fraction: 1 };
  }

  const fraction = Math.min(1, Math.max(0, (rep - floor) / (need - floor)));
  return { tier, name, rep, nextTier, repToNext: Math.max(0, need - rep), fraction };
}
